// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Amending a college's name or address (**ADR-0076** §4e).
 *
 * Two fields, and the absent third is the point. The college code is not
 * editable here because the tenant identifier is derived from it: renaming a
 * college mid-flight would strand the appliance holding the old identifier, so
 * a wrong code is corrected by deleting the registration and registering it
 * again under a different one. The client port cannot carry a code on this
 * operation and the BFF refuses an unknown member outright; this form simply
 * does not offer one.
 *
 * Client validation is an affordance, never the decision (`CST-048`). Every
 * bound below is also enforced upstream, and a `422` is rendered as guidance
 * rather than treated as impossible.
 */

import type { TenantOverview } from '@eai/ui-shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { MAX_ADDRESS_LENGTH, MAX_NAME_LENGTH } from './AddCollegePanel';

/** What an amendment carries, before the client adds the identifier. */
export interface CollegeAmendment {
  readonly name: string;
  readonly address: string;
}

/** Props for {@link EditCollegeDialog}. */
export interface EditCollegeDialogProps {
  /** The college being amended. */
  readonly college: TenantOverview;
  /** Whether a submission is in flight; the form is inert while it is. */
  readonly submitting: boolean;
  /** Sanitised failure text from the last attempt, if any. */
  readonly failure: string | null;
  /** Submit the amendment. */
  readonly onSubmit: (amendment: CollegeAmendment) => void;
  /** Close without amending. */
  readonly onClose: () => void;
}

/** Field-level guidance produced here. */
export interface AmendmentErrors {
  readonly name?: string;
  readonly address?: string;
}

/** Validate what can be checked here. The server checks all of it again. */
export function validateAmendment(name: string, address: string): AmendmentErrors {
  const errors: { name?: string; address?: string } = {};
  if (name.trim().length === 0) {
    errors.name = 'Enter the college name.';
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.name = `The college name may be at most ${String(MAX_NAME_LENGTH)} characters.`;
  }
  if (address.length > MAX_ADDRESS_LENGTH) {
    errors.address = `The address may be at most ${String(MAX_ADDRESS_LENGTH)} characters.`;
  }
  return errors;
}

const TITLE_ID = 'edit-college-title';

/** Amend a college's name or address. */
export function EditCollegeDialog({
  college,
  submitting,
  failure,
  onSubmit,
  onClose,
}: EditCollegeDialogProps): ReactNode {
  const [name, setName] = useState(college.name);
  const [address, setAddress] = useState(college.address);
  const [errors, setErrors] = useState<AmendmentErrors>({});
  const nameRef = useRef<HTMLInputElement | null>(null);

  // Focus enters on the first editable field, so an operator using a screen
  // reader is told what this dialog is for rather than left on its container.
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const submit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      // A second submission while the first is in flight is not a second
      // amendment. The parent also refuses one; refusing here keeps a
      // double-press from clearing the failure the operator is reading.
      if (submitting) {
        return;
      }
      const found = validateAmendment(name, address);
      setErrors(found);
      if (Object.keys(found).length > 0) {
        return;
      }
      onSubmit({ name: name.trim(), address: address.trim() });
    },
    [address, name, onSubmit, submitting],
  );

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle id={TITLE_ID}>Edit {college.name}</DialogTitle>
      <Box component="form" onSubmit={submit} noValidate>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The college code ({college.code}) cannot be changed: the tenant identifier is derived
            from it. Correct a wrong code by deleting the registration and registering the college
            again under a different code.
          </Typography>
          {failure === null ? null : (
            <Alert severity="error" role="alert" sx={{ mb: 2 }} data-testid="edit-college-error">
              {failure}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="College name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              required
              inputRef={nameRef}
              disabled={submitting}
              error={errors.name !== undefined}
              helperText={errors.name ?? ' '}
              slotProps={{ htmlInput: { maxLength: MAX_NAME_LENGTH } }}
            />
            <TextField
              label="Address"
              value={address}
              onChange={(event) => {
                setAddress(event.target.value);
              }}
              disabled={submitting}
              error={errors.address !== undefined}
              helperText={errors.address ?? ' '}
              slotProps={{ htmlInput: { maxLength: MAX_ADDRESS_LENGTH } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
